/**
 * Showroom checks (owner, 23 Oct 2026: "in every mitra portal also we need
 * location verification from them and a picture of marketing board we sent
 * us to verify they are using it or not at their showroom") — the reads and
 * writes the counter, its ASM and the office share.
 *
 * The counter's own calls pin `book: null`: one shop is one showroom whichever
 * book the switch shows. The office's go to the book it stands in, or the one
 * an approvals row names.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { isAsmRole, type MyShowroom, type ShowroomCheckRow, type ShowroomCoverage } from '@bright/shared';

import { api } from '@/lib/api';
import { useAuth, usePermission } from '@/lib/auth';
import type { BookKey } from '@/lib/book';
import { approvalKeys } from '@/features/approvals/approvals-api';

export const showroomKeys = {
  all: ['showrooms'] as const,
  mine: ['showrooms', 'mine'] as const,
  coverage: ['showrooms', 'coverage'] as const,
  party: (partyId: string) => ['showrooms', 'party', partyId] as const,
};

/** Reads the coverage: the office (`field:review`) and the ASMs, whose counters it is. */
export function useReadsShowrooms(): boolean {
  const { user } = useAuth();
  const reviews = usePermission('field:review');
  return Boolean(user) && (reviews || isAsmRole(user?.role.key));
}

export function useMyShowroom(enabled = true) {
  return useQuery({
    queryKey: showroomKeys.mine,
    queryFn: () => api.get<MyShowroom>('/showrooms/mine', undefined, { book: null }),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useShowroomCoverage(enabled = true) {
  return useQuery({
    queryKey: showroomKeys.coverage,
    queryFn: () => api.get<ShowroomCoverage>('/showrooms/coverage'),
    enabled,
    refetchOnWindowFocus: true,
  });
}

export interface ShowroomHistory {
  applies: boolean;
  mayAnswer: boolean;
  checks: ShowroomCheckRow[];
}

/** A showroom's checks, newest first. `book` is the one an approvals row names; left out, the book the switch shows. */
export function useShowroomHistory(partyId: string | null, book?: BookKey | null) {
  return useQuery({
    queryKey: [...showroomKeys.party(partyId ?? ''), book ?? 'active'],
    queryFn: () => api.get<ShowroomHistory>(`/showrooms/party/${partyId}`, undefined, book === undefined ? {} : { book }),
    enabled: Boolean(partyId),
    retry: false,
  });
}

/** Step 1: the location and the shop's photo — the check is then pending its board (26 Oct 2026). */
export function sendShowroomCheck(body: FormData): Promise<ShowroomCheckRow> {
  return api.post<ShowroomCheckRow>('/showrooms/mine', body, { book: null });
}

/** Step 2: the board's photo, on the pending check — it then goes for checking. */
export function sendShowroomBoard(body: FormData): Promise<ShowroomCheckRow> {
  return api.post<ShowroomCheckRow>('/showrooms/mine/board', body, { book: null });
}

export function answerShowroomCheck(id: string, answer: { status: 'VERIFIED' | 'REJECTED'; note?: string | null }, book?: BookKey | null): Promise<ShowroomCheckRow> {
  return api.post<ShowroomCheckRow>(`/showrooms/checks/${id}/review`, answer, book === undefined ? {} : { book });
}

/** The shop's photo (step 1), or the board's (step 2). */
export const showroomPhotoPath = (id: string, which: 'shop' | 'board' = 'shop') => `/showrooms/checks/${id}/photo?which=${which}`;

/** After a check is sent or answered: every list of checks, and the approvals counts. */
export function refreshShowrooms(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: showroomKeys.all });
  void queryClient.invalidateQueries({ queryKey: approvalKeys.all });
}
